// ItemsIndex.js
// プロジェクトの items(マーカー/描画/図形/ファイル/写真)を1つの JSON にまとめた「索引ファイル」。
// オーナーが更新時に Storage へ書き出し、オーナー以外の閲覧者は Firestore を全件読む代わりに
// このファイルを1回ダウンロードして描画する（Firestore の read を item 数に比例させない）。
// 索引作成後の変更分は、従来のリスナー（updatetime >= 索引時刻）で拾う。
import { Timestamp } from 'firebase/firestore';

export const ITEMS_INDEX_VERSION = 1;
export const ITEMS_INDEX_FILE = 'items_index.json';
// 変更後、この時間だけ待って書く（連続編集をまとめる）
export const ITEMS_INDEX_DEBOUNCE_MS = 5000;
// 連続編集が続いても最低この間隔で1回は書く
export const ITEMS_INDEX_MAX_WAIT_MS = 30000;
export const ITEMS_INDEX_FETCH_TIMEOUT_MS = 8000;
// 閲覧者のリスナー開始時刻 = 索引時刻 − この余裕（端末間の時計ずれ・書き込み途中の分を取りこぼさない）
export const ITEMS_INDEX_LISTENER_MARGIN_MS = 60000;
// 閲覧者が索引の有無を知るためにプロジェクト doc の到着を待つ上限
export const PROJECT_DOC_WAIT_MS = 4000;

// Storage 上の索引ファイルのパス（オーナーだけが書け、誰でも読める場所）
export function itemsIndexStoragePath(owneruid, uuid) {
	return `files/${owneruid}/${uuid}/${owneruid}/open/${ITEMS_INDEX_FILE}`;
}

function isTimestampLike(v) {
	return v && typeof v === 'object' && typeof v.toDate === 'function' && typeof v.seconds === 'number';
}

// Firestore Timestamp を {"$ts": ISO} にタグ付けして JSON 化できる形にする（再帰）。
// 文字列の日時（写真の dateTimeOriginal 等）と区別するためタグ付きにする。
export function serializeFirestoreValue(v) {
	if (v === null || v === undefined) return v;
	if (isTimestampLike(v)) return { $ts: v.toDate().toISOString() };
	if (v instanceof Date) return { $ts: v.toISOString() };
	if (Array.isArray(v)) return v.map(serializeFirestoreValue);
	if (typeof v === 'object') {
		const out = {};
		for (const k of Object.keys(v)) out[k] = serializeFirestoreValue(v[k]);
		return out;
	}
	return v;
}

// {"$ts": ISO} を Firestore Timestamp に戻す（再帰）。processProjectItem 側が
// updatetime.toDate() を呼ぶので、Firestore から読んだときと同じ型にしておく。
export function reviveFirestoreValue(v) {
	if (v === null || v === undefined) return v;
	if (Array.isArray(v)) return v.map(reviveFirestoreValue);
	if (typeof v === 'object') {
		const keys = Object.keys(v);
		if (keys.length === 1 && keys[0] === '$ts' && typeof v.$ts === 'string') {
			const d = new Date(v.$ts);
			return isNaN(d.getTime()) ? null : Timestamp.fromDate(d);
		}
		const out = {};
		for (const k of keys) out[k] = reviveFirestoreValue(v[k]);
		return out;
	}
	return v;
}

function toMillis(v) {
	if (!v) return 0;
	if (isTimestampLike(v)) return v.toMillis ? v.toMillis() : v.seconds * 1000;
	if (v instanceof Date) return v.getTime();
	const t = new Date(v).getTime();
	return isNaN(t) ? 0 : t;
}

export function updatetimeMillis(data) {
	return data ? toMillis(data.updatetime) : 0;
}

// itemDocs: Map(docId -> 生データ)。削除予約の墓石(isRemove)は含めない。
export function liveItemCount(itemDocs) {
	let n = 0;
	itemDocs.forEach((data) => { if (data && !data.isRemove) n++; });
	return n;
}

export function maxUpdatetimeMillis(itemDocs) {
	let max = 0;
	itemDocs.forEach((data) => {
		if (!data || data.isRemove) return;
		const t = updatetimeMillis(data);
		if (t > max) max = t;
	});
	return max;
}

export function buildItemsIndexJson({ owneruid, uuid, itemDocs, generatedAt }) {
	const items = [];
	itemDocs.forEach((data, id) => {
		if (!data || data.isRemove) return;
		items.push({ id, ...serializeFirestoreValue(data) });
	});
	return {
		version: ITEMS_INDEX_VERSION,
		generatedAt: generatedAt.toISOString(),
		owneruid,
		uuid,
		count: items.length,
		items,
	};
}

// 索引 JSON を検証して [{id, data}] に戻す。不正なら throw（呼び出し側で全件読みにフォールバック）。
export function parseItemsIndexJson(obj, { uuid }) {
	if (!obj || typeof obj !== 'object') throw new Error('index: not an object');
	if (obj.version !== ITEMS_INDEX_VERSION) throw new Error(`index: unsupported version ${obj.version}`);
	if (uuid && obj.uuid !== uuid) throw new Error('index: uuid mismatch');
	if (!Array.isArray(obj.items)) throw new Error('index: items missing');
	const entries = [];
	for (const raw of obj.items) {
		if (!raw || typeof raw !== 'object' || typeof raw.id !== 'string') continue;
		const { id, ...rest } = raw;
		const data = reviveFirestoreValue(rest);
		if (!data || data.isRemove) continue;
		entries.push({ id, data });
	}
	return entries;
}
